'use client';

import Link from 'next/link';
import Image from 'next/image';
import { Calendar, Clock, Users, Radio, Trophy, ShoppingCart } from 'lucide-react';
import { LiveEventPublic } from '@/lib/api';
import { useTranslations } from 'next-intl';
import { PAGE_CONTAINER } from './ui';

const DASHBOARD_URL = process.env.NEXT_PUBLIC_DASHBOARD_URL || 'http://localhost:3502';

function formatShortDate(date: string) {
  return new Intl.DateTimeFormat('id-ID', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
    timeZone: 'Asia/Jakarta',
    timeZoneName: 'short',
  }).format(new Date(date));
}

function formatDuration(startAt: string, endAt: string, startMode: string) {
  const mins = Math.floor((new Date(endAt).getTime() - new Date(startAt).getTime()) / 60000);
  if (startMode === 'FLEXIBLE') {
    const fmt = (d: string) => new Intl.DateTimeFormat('id-ID', {
      hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Jakarta',
    }).format(new Date(d));
    return `Fleksibel · ${fmt(startAt)}–${fmt(endAt)} WIB`;
  }
  if (mins >= 60) {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m > 0 ? `${h} jam ${m} menit` : `${h} jam`;
  }
  return `${mins} menit`;
}

function EventCard({ event }: { event: LiveEventPublic }) {
  const t = useTranslations('home.liveEvents');
  const isLive = event.status === 'LIVE';
  const isCompleted = event.status === 'COMPLETED';
  const accentColor = event.category.color ?? '#0f766e';
  const pricing = event.pricing;
  const hasDiscount = pricing != null && pricing.promoPrice != null && pricing.promoPrice < pricing.basePrice;
  const discountPct = hasDiscount
    ? Math.round(((pricing!.basePrice - pricing!.promoPrice!) / pricing!.basePrice) * 100)
    : 0;

  return (
    <div className="group relative flex flex-col sm:flex-row bg-white rounded-2xl border border-slate-200 hover:border-teal-300 hover:shadow-lg transition-all overflow-hidden">
      {/* Card-wide navigation. Overlaid rather than wrapping the card so the
          status CTAs below can stay real anchors without nesting <a> in <a>
          (which the HTML parser hoists out, breaking hydration). */}
      <Link href={`/events/${event.slug}`} className="absolute inset-0 z-10">
        <span className="sr-only">{event.title}</span>
      </Link>

      {/* Thumbnail — clean, no overlays */}
      <div className="relative sm:w-56 lg:w-72 shrink-0 aspect-video sm:aspect-auto overflow-hidden bg-slate-100">
        {event.coverImageUrl ? (
          <Image
            src={event.coverImageUrl}
            alt={event.title}
            fill
            sizes="(max-width: 640px) 100vw, 288px"
            className="object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center"
            style={{ background: `${accentColor}30` }}
          >
            <Radio className="w-10 h-10 opacity-30" style={{ color: accentColor }} />
          </div>
        )}
        {/* LIVE pulse — only status overlay kept, minimal */}
        {isLive && (
          <div className="absolute top-3 left-3">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-red-500 text-white shadow-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
              LIVE
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4 sm:p-5 flex flex-col flex-1 justify-center min-w-0">
        {/* Badges row */}
        <div className="flex items-center gap-1.5 flex-wrap mb-2">
          <span
            className="text-[10px] font-semibold px-1.5 py-0.5 rounded"
            style={{ backgroundColor: `${accentColor}15`, color: accentColor }}
          >
            {event.category.name}
          </span>
          {isCompleted ? (
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{t('selesai')}</span>
          ) : !isLive && (
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-teal-50 text-teal-700">{t('mendatang')}</span>
          )}
          {event.isFree ? (
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-600">{t('gratis')}</span>
          ) : (
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-50 text-amber-600">{t('berbayar')}</span>
          )}
        </div>

        <h3 className="font-bold text-slate-900 text-base sm:text-lg leading-snug mb-3 line-clamp-2 group-hover:text-teal-700 transition-colors">
          {event.title}
        </h3>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500 mb-4">
          <span className="flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5 text-slate-400" />
            {formatShortDate(event.startAt)}
          </span>
          <span className="flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5 text-slate-400" />
            {formatDuration(event.startAt, event.endAt, event.startMode)}
          </span>
          {event.maxParticipants && (
            <span className="flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5 text-slate-400" />
              {t('maks', { count: event.maxParticipants })}
            </span>
          )}
        </div>

        {/* Price + CTA */}
        <div className="flex items-center justify-between gap-3 pt-3 border-t border-slate-100 mt-auto">
          <div>
            {event.isFree ? (
              <span className="font-bold text-base text-emerald-600">{t('gratis')}</span>
            ) : hasDiscount ? (
              <span className="flex items-center gap-1.5 flex-wrap">
                <span className="text-[11px] font-bold px-1.5 py-0.5 rounded bg-red-500 text-white shrink-0">-{discountPct}%</span>
                <span className="flex items-baseline gap-1">
                  <span className="font-bold text-base text-orange-600">Rp&nbsp;{pricing!.promoPrice!.toLocaleString('id-ID')}</span>
                  <span className="text-xs text-slate-400 line-through">Rp&nbsp;{pricing!.basePrice.toLocaleString('id-ID')}</span>
                </span>
              </span>
            ) : pricing ? (
              <span className="font-bold text-base text-slate-900">Rp&nbsp;{pricing.basePrice.toLocaleString('id-ID')}</span>
            ) : null}
          </div>

          {isCompleted ? (
            <Link
              href={`/events/${event.slug}/leaderboard`}
              className="relative z-20 flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors"
            >
              <Trophy className="w-3.5 h-3.5" /> Leaderboard
            </Link>
          ) : isLive ? (
            <a
              href={`${DASHBOARD_URL}/events/${event.slug}`}
              className="relative z-20 flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-red-500 hover:bg-red-600 text-white transition-colors"
            >
              <Radio className="w-3.5 h-3.5" /> {t('mulai')}
            </a>
          ) : (
            // Non-interactive by choice: the card-wide overlay <Link> already
            // navigates to the event detail page, where registration happens.
            <span
              className={`flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg transition-colors ${
                event.isFree
                  ? 'text-teal-600 bg-teal-50 hover:bg-teal-100'
                  : 'text-orange-600 bg-orange-50 hover:bg-orange-100'
              }`}
            >
              <ShoppingCart className="w-3.5 h-3.5" />
              {event.isFree ? t('daftar') : t('beliTiket')}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export function LiveEvents({ events }: { events: LiveEventPublic[] }) {
  const t = useTranslations('home.liveEvents');

  if (events.length === 0) return null;

  return (
    <section className="py-10 bg-white">
      <div className={PAGE_CONTAINER}>
        <div className="flex items-start justify-between mb-6 gap-4">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900">{t('title')}</h2>
            <p className="text-sm text-slate-500 mt-1">{t('description')}</p>
          </div>
          <Link
            href="/events"
            className="text-sm font-medium text-teal-600 hover:text-teal-700 transition-colors shrink-0 mt-1"
          >
            {t('lihatSemua')}
          </Link>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          {events.slice(0, 4).map((event) => (
            <EventCard key={event.id} event={event} />
          ))}
        </div>
      </div>
    </section>
  );
}
